import {
  asArray,
  asBoolean,
  asNumber,
  asString,
  asStringList,
  isRecord,
} from '@flowx/designer/modules/ai-builder/utils/payload.util'

/**
 * The `ask_user` payload, normalized for rendering.
 *
 * The third interrupt type, read the way the other two are. Its payload used to be picked apart where
 * it was needed — the interrupt component, the rail's placeholder, and the store's `pick()` — and the
 * two readings of `options` had already drifted apart on what counts as one. A field the harness
 * adds belongs in one file, not three.
 */
export type QuestionDisplay = {
  question: string
  /** Answers to click. Frequently absent, and only useful when it holds strings. */
  options: string[]
  recommended: string | null
  storyId: string | null
  /** What the harness's own checks found wrong with the question and could not fix. A question is
   * rewritten up to twice before this, so showing it means the maker can still answer knowing the
   * agent's own reader would not have. */
  hardFailures: string[]
  /** Softer findings on the question, worth a quiet line rather than a callout. */
  warnings: string[]
}

export function toQuestionDisplay(payload: unknown): QuestionDisplay {
  const record = isRecord(payload) ? payload : {}

  return {
    question: asString(record['question']),
    options: asStringList(record['options']),
    recommended: recommendedOption(record['recommended'], record['options']),
    storyId:
      typeof record['story_id'] === 'string' ? asString(record['story_id']).trim() || null : null,
    hardFailures: asStringList(record['hard_failures']),
    warnings: asStringList(record['gate_warnings']),
  }
}

/** The recommended option: given as its text by a story question, or as the index of the option
 * an `ask_user` question shows. */
function recommendedOption(recommended: unknown, options: unknown): string | null {
  const option = Number.isInteger(recommended)
    ? asArray(options)[recommended as number]
    : recommended

  return typeof option === 'string' ? option.trim() || null : null
}

/**
 * A `specialist_review` payload, read for the confirm card.
 *
 * `effects` is the harness's own prose — it already words the commit and no-commit cases
 * differently — so it is shown as written rather than rebuilt from the flags beside it. The flags
 * are read anyway, because `commit` is the difference between a WIP branch and a certified write
 * and belongs on screen as a fact of its own.
 */
export type SpecialistDisplay = {
  /** e.g. `writer.application_descriptions.v1`. */
  capability: string
  /** What running this does, in the harness's words. */
  effects: string
  /** The branch the new version is created on. */
  branch: string
  /** The committed version the work starts from. */
  sourceAppVersionId: string
  createsVersion: boolean
  persists: boolean
  /** False on the default path: the write lands on a WIP branch and is not committed. */
  commits: boolean
}

export function toSpecialistDisplay(payload: unknown): SpecialistDisplay {
  const record = isRecord(payload) ? payload : {}

  return {
    capability: asString(record['capability']),
    effects: asString(record['effects']),
    branch: asString(record['branch']),
    sourceAppVersionId: asString(record['source_app_version_id']),
    createsVersion: asBoolean(record['create_version'], false),
    persists: asBoolean(record['persist'], false),
    commits: asBoolean(record['commit'], false),
  }
}

export type MergeChoice = 'branch' | 'main' | 'proposal'

const MERGE_CHOICES: readonly MergeChoice[] = ['branch', 'main', 'proposal']

export type MergeConflictOption = {
  id: MergeChoice
  label: string
  keeps: string
  loses: string
}

/** What the right side opens beside the question: the conflicting resource on both sides of the
 * merge, since the merged version stays unusable until the merge is finalized. */
export type MergeConflictFocus = {
  appId: string
  resourceType: string
  resourceDefinitionId: string
  nodePath: string
  branchVersionId: string
  mainVersionId: string
}

/** A `merge_conflict` payload: one conflict of a harness-run branch merge, asked as a question. */
export type MergeConflictDisplay = {
  type: string
  question: string
  options: MergeConflictOption[]
  recommended: MergeChoice | null
  /** Conflicts still to be asked after this one. */
  remaining: number
  focus: MergeConflictFocus | null
}

function isMergeChoice(value: unknown): value is MergeChoice {
  return MERGE_CHOICES.includes(value as MergeChoice)
}

export function toMergeConflictDisplay(payload: unknown): MergeConflictDisplay {
  const record = isRecord(payload) ? payload : {}
  const focus = isRecord(record['focus']) ? record['focus'] : null

  return {
    type: asString(record['type']),
    question: asString(record['question']),
    options: asArray(record['options'])
      .filter(isRecord)
      .filter((option) => isMergeChoice(option['id']))
      .map((option) => ({
        id: option['id'] as MergeChoice,
        label: asString(option['label']),
        keeps: asString(option['keeps']),
        loses: asString(option['loses']),
      })),
    recommended: isMergeChoice(record['recommended']) ? record['recommended'] : null,
    remaining: asNumber(record['remaining'], 0),
    focus: focus && {
      appId: asString(focus['app_id']),
      resourceType: asString(focus['resource_type']),
      resourceDefinitionId: asString(focus['resource_definition_id']),
      nodePath: asString(focus['node_path'] ?? record['node_path']),
      branchVersionId: asString(focus['branch_version_id']),
      mainVersionId: asString(focus['main_version_id']),
    },
  }
}
